import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { generatePastelColor, generateSaturatedColor } from '../../Utils/colorUtils';
import TagsDropdown from '../TagsDropdown';

const escapeHtml = (text) => {
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
  return String(text).replace(/[&<>"']/g, (m) => map[m]);
};

const TagEditor = forwardRef(({
  value = '',
  onChange,
  placeholder = '',
  onTagClick,
  tags,
  scope,
  className = '',
  singleLine,
  multiline = true,
  minHeight,
  minRows,
  maxRows
}, ref) => {
  const rootRef = useRef(null);
  const { t } = useTranslation();
  const [tagMenuAnchorEl, setTagMenuAnchorEl] = useState(null);
  const [triggeredByBrace, setTriggeredByBrace] = useState(false);
  const [escapeDialogOpen, setEscapeDialogOpen] = useState(false);
  const [escapeTarget, setEscapeTarget] = useState(null);
  const savedSelectionRef = useRef(null);
  const isSingleLine = typeof singleLine === 'boolean' ? singleLine : !multiline;
  const normalizeForMode = (text) => {
    if (!text) return '';
    if (!isSingleLine) return text;
    return String(text).replace(/\s*\r?\n+\s*/g, ' ');
  };
  const isBlankText = (text) => {
    if (!text) return true;
    return String(text).replace(/\u00A0/g, ' ').trim().length === 0;
  };
  const normalizedValue = normalizeForMode(value || '');
  const displayValue = isBlankText(normalizedValue) ? '' : normalizedValue;
  const [isEmpty, setIsEmpty] = useState(isBlankText(displayValue));
  const computedMinHeight = minHeight || (minRows ? `calc(${minRows} * 1.5em + 1.25rem)` : (isSingleLine ? '40px' : '120px'));
  const computedMaxHeight = maxRows ? `calc(${maxRows} * 1.5em + 1.25rem)` : undefined;
  const isApplyingRef = useRef(false);
  const caretMarker = '__TAG_EDITOR_CARET__';
  const tagPattern = /\{[a-zA-Z0-9_]+\}/;
  const tagRegex = /\{\{([a-zA-Z0-9_]+)\}\}|\{([a-zA-Z0-9_]+)\}/g;

  const buildTagSpanHtml = (tagName, escaped = false) => {
    const bgColor = generatePastelColor(tagName);
    const color = generateSaturatedColor(tagName);
    const style = escaped
      ? `background-color: transparent; color: ${color}; border: 1px dashed ${color};`
      : `background-color: ${bgColor}; color: ${color};`;
    const escapedAttr = escaped ? ' data-escaped="true"' : '';
    const className = escaped ? 'tag-chip tag-chip-escaped' : 'tag-chip';
    return `<span class="${className}" data-tag="${escapeHtml(tagName)}"${escapedAttr} contenteditable="false" style="${style}">{${escapeHtml(tagName)}}</span>`;
  };

  // Convert plain text (with `{tag}`) into HTML with span tags
  const toHtml = (text) => {
    const normalizedText = normalizeForMode(text);
    if (!normalizedText) return '';
    
    // First, handle newlines by converting them to <br> tags
    // We'll process line by line
    const lines = isSingleLine ? [normalizedText] : normalizedText.split('\n');
    let html = '';
    
    lines.forEach((line, lineIndex) => {
      tagRegex.lastIndex = 0;
      let lastIndex = 0;
      let match;
      
      while ((match = tagRegex.exec(line)) !== null) {
        const idx = match.index;
        if (idx > lastIndex) {
          html += escapeHtml(line.substring(lastIndex, idx));
        }
        const tagName = match[1] || match[2];
        const isEscaped = Boolean(match[1]);
        html += buildTagSpanHtml(tagName, isEscaped);
        lastIndex = tagRegex.lastIndex;
      }
      if (lastIndex < line.length) {
        html += escapeHtml(line.substring(lastIndex));
      }
      
      // Add <br> for newlines (except after the last line)
      if (lineIndex < lines.length - 1) {
        html += '<br>';
      }
    });
    
    return html;
  };

  const parseFromDom = (el) => {
    if (!el) return '';
    
    // Recursive function to properly handle nested elements and preserve newlines
    const extractText = (node) => {
      let result = '';
      
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.nodeValue;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName?.toLowerCase();
        
        // Handle tag chips
        if (node.getAttribute && node.getAttribute('data-tag')) {
          const tagName = node.getAttribute('data-tag');
          const isEscaped = node.getAttribute('data-escaped') === 'true' || node.classList?.contains('tag-chip-escaped');
          return isEscaped ? `{{${tagName}}}` : `{${tagName}}`;
        }
        // Handle <br> elements - convert to newline
        else if (tagName === 'br') {
          result += '\n';
        }
        // Handle block elements (div, p) - add newline before content if not first
        else if (tagName === 'div' || tagName === 'p') {
          // Check if this div/p has actual content
          const hasContent = node.childNodes.length > 0;
          if (hasContent) {
            // Add newline before block element (except if it's the first child or previous sibling already added one)
            const prevSibling = node.previousSibling;
            const isFirstChild = !prevSibling;
            const prevEndsWithNewline = result.endsWith('\n');
            
            if (!isFirstChild && !prevEndsWithNewline) {
              result += '\n';
            }
            
            // Process children
            node.childNodes.forEach((child) => {
              result += extractText(child);
            });
          }
        }
        // Handle other elements - just process children
        else {
          node.childNodes.forEach((child) => {
            result += extractText(child);
          });
        }
      }
      
      return result;
    };
    
    let text = '';
    el.childNodes.forEach((node) => {
      text += extractText(node);
    });
    
    return normalizeForMode(text);
  };

  const hasRawTagPattern = (el) => {
    if (!el) return false;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (node.parentNode && node.parentNode.closest && node.parentNode.closest('.tag-chip')) continue;
      if (tagPattern.test(node.nodeValue || '')) return true;
    }
    return false;
  };

  const getCaretOffset = () => {
    const root = rootRef.current;
    if (!root) return null;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.startContainer)) return null;
    const pre = range.cloneRange();
    pre.selectNodeContents(root);
    pre.setEnd(range.startContainer, range.startOffset);
    return pre.toString().length;
  };

  const restoreCaretFromMarker = () => {
    const root = rootRef.current;
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const value = node.nodeValue || '';
      const idx = value.indexOf(caretMarker);
      if (idx === -1) continue;
      const before = value.slice(0, idx);
      const after = value.slice(idx + caretMarker.length);
      node.nodeValue = before + after;
      const sel = window.getSelection();
      if (!sel) return;
      const range = document.createRange();
      range.setStart(node, before.length);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    // Fallback: place caret at end
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  };

  // Expose API to parent
  useImperativeHandle(ref, () => ({
    insertTag: (tagName) => insertTagAtCursor(tagName),
    focus: () => rootRef.current && rootRef.current.focus(),
    getValue: () => parseFromDom(rootRef.current)
  }));

  useEffect(() => {
    // Only update DOM if value differs
    if (!rootRef.current) return;
    const currentText = parseFromDom(rootRef.current);
    if (currentText !== displayValue) {
      rootRef.current.innerHTML = toHtml(displayValue);
    }
    setIsEmpty(isBlankText(displayValue));
  }, [displayValue]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const handleInput = () => {
      if (isApplyingRef.current) {
        isApplyingRef.current = false;
        return;
      }
      const text = parseFromDom(root);
      const normalizedText = isBlankText(text) ? '' : text;
      onChange && onChange(normalizedText);
      setIsEmpty(isBlankText(normalizedText));

      if (isBlankText(normalizedText)) {
        if (root.innerHTML !== '') {
          isApplyingRef.current = true;
          root.innerHTML = '';
        }
        return;
      }

      if (hasRawTagPattern(root)) {
        const caretOffset = getCaretOffset();
        const safeOffset = typeof caretOffset === 'number'
          ? Math.min(Math.max(caretOffset, 0), normalizedText.length)
          : normalizedText.length;
        const markedText = `${normalizedText.slice(0, safeOffset)}${caretMarker}${normalizedText.slice(safeOffset)}`;
        const html = toHtml(markedText);
        if (root.innerHTML !== html) {
          isApplyingRef.current = true;
          root.innerHTML = html;
          restoreCaretFromMarker();
        }
      }
    };
    const handleClick = (e) => {
      const el = e.target.closest && e.target.closest('.tag-chip');
      if (el && el.dataset && el.dataset.tag) {
        e.preventDefault();
        e.stopPropagation();
        setEscapeTarget({
          tagName: el.dataset.tag,
          node: el,
          escaped: el.dataset.escaped === 'true'
        });
        setEscapeDialogOpen(true);
        return;
      }
      saveSelection();
    };
    const handlePaste = (e) => {
      e.preventDefault();
      const text = normalizeForMode((e.clipboardData || window.clipboardData).getData('text'));
      const html = toHtml(text);
      
      // Use insertHTML to preserve the tag chips structure
      if (document.queryCommandSupported('insertHTML')) {
        document.execCommand('insertHTML', false, html);
      } else {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        selection.deleteFromDocument();
        const div = document.createElement('div');
        div.innerHTML = html;
        const fragment = document.createDocumentFragment();
        while (div.firstChild) {
          fragment.appendChild(div.firstChild);
        }
        selection.getRangeAt(0).insertNode(fragment);
        selection.collapseToEnd();
      }
      handleInput();
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && isSingleLine) {
        e.preventDefault();
      }

      saveSelection();
      
      // Open tag menu when typing '{'
      if (e.key === '{' && tags && !tagMenuAnchorEl) {
        // We use setTimeout to allow the '{' character to be inserted first,
        // then we capture the selection right after it.
        setTimeout(() => {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0 && rootRef.current && rootRef.current.contains(sel.anchorNode)) {
            savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
            setTagMenuAnchorEl(rootRef.current);
            setTriggeredByBrace(true);
          }
        }, 0);
      }
    };
    const handleBeforeInput = (e) => {
      if (!isSingleLine) return;
      if (e.inputType === 'insertParagraph' || e.inputType === 'insertLineBreak') {
        e.preventDefault();
      }
    };

    const handleKeyUp = () => {
      saveSelection();
    };

    const handleMouseUp = () => {
      saveSelection();
    };

    // Save current selection state
    const saveSelection = () => {
       const sel = window.getSelection();
       if (sel && sel.rangeCount > 0 && root && root.contains(sel.anchorNode)) {
          savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
       }
    };
    
    root.addEventListener('input', handleInput);
    root.addEventListener('click', handleClick);
    root.addEventListener('paste', handlePaste);
    root.addEventListener('keydown', handleKeyDown);
    root.addEventListener('keyup', handleKeyUp);
    root.addEventListener('mouseup', handleMouseUp);
    root.addEventListener('beforeinput', handleBeforeInput);
    return () => {
      root.removeEventListener('input', handleInput);
      root.removeEventListener('click', handleClick);
      root.removeEventListener('paste', handlePaste);
      root.removeEventListener('keydown', handleKeyDown);
      root.removeEventListener('keyup', handleKeyUp);
      root.removeEventListener('mouseup', handleMouseUp);
      root.removeEventListener('beforeinput', handleBeforeInput);
    };
  }, [onChange, isSingleLine, tags, tagMenuAnchorEl]);

  const closeEscapeDialog = () => {
    setEscapeDialogOpen(false);
    setEscapeTarget(null);
  };

  const buildTagSpan = (tagName, escaped = false) => {
    const span = document.createElement('span');
    span.className = escaped ? 'tag-chip tag-chip-escaped' : 'tag-chip';
    span.setAttribute('data-tag', tagName);
    if (escaped) {
      span.setAttribute('data-escaped', 'true');
    }
    span.setAttribute('contenteditable', 'false');
    const color = generateSaturatedColor(tagName);
    if (escaped) {
      span.style.backgroundColor = 'transparent';
      span.style.color = color;
      span.style.border = `1px dashed ${color}`;
    } else {
      span.style.backgroundColor = generatePastelColor(tagName);
      span.style.color = color;
    }
    span.textContent = `{${tagName}}`;
    return span;
  };

  const handleEscapeConfirm = () => {
    if (!escapeTarget?.node || !escapeTarget?.tagName) {
      closeEscapeDialog();
      return;
    }

    const { tagName, node, escaped } = escapeTarget;
    const updatedSpan = buildTagSpan(tagName, !escaped);
    node.replaceWith(updatedSpan);

    if (rootRef.current) {
      rootRef.current.focus();
      const sel = window.getSelection();
      if (sel) {
        const range = document.createRange();
        range.setStartAfter(updatedSpan);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      const event = new Event('input', { bubbles: true });
      rootRef.current.dispatchEvent(event);
    }

    closeEscapeDialog();
  };

  const insertTagAtCursor = (tagName) => {
    const sel = window.getSelection();
    if (!sel || !sel.getRangeAt || !rootRef.current) return;
    
    let range;
    
    // Check if current selection is valid and inside editor
    if (sel.rangeCount > 0 && rootRef.current.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      range = sel.getRangeAt(0);
    }
    // If not, try to use saved selection
    else if (savedSelectionRef.current) {
       range = savedSelectionRef.current;
       sel.removeAllRanges();
       sel.addRange(range);
    }
    
    // If still no valid range inside editor, fallback to end
    if (!range || !rootRef.current.contains(range.commonAncestorContainer)) {
       rootRef.current.focus();
       range = document.createRange();
       range.selectNodeContents(rootRef.current);
       range.collapse(false);
       sel.removeAllRanges();
       sel.addRange(range);
    }
    
    const span = document.createElement('span');
    span.className = 'tag-chip';
    span.setAttribute('data-tag', tagName);
    span.setAttribute('contenteditable', 'false');
    span.style.backgroundColor = generatePastelColor(tagName);
    span.style.color = generateSaturatedColor(tagName);
    span.textContent = `{${tagName}}`;
    range.deleteContents();
    range.insertNode(span);
    // Position caret after the inserted text node
    const newRange = document.createRange();
    newRange.setStartAfter(span);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    
    // Trigger change - manually dispatch input event so handleInput runs
    if (rootRef.current) {
      const event = new Event('input', { bubbles: true });
      rootRef.current.dispatchEvent(event);
    }
  };

  const handleInternalTagClick = (event) => {
    setTagMenuAnchorEl(event.currentTarget);
  };

  const handleInternalTagSelect = (tagValue) => {
    if (triggeredByBrace && savedSelectionRef.current) {
      try {
        // Restore range
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedSelectionRef.current);
        
        // Expand range to include preceding '{'
        const range = sel.getRangeAt(0);
        if (range.startOffset > 0) {
          range.setStart(range.startContainer, range.startOffset - 1);
          // Optional: verify content is '{' if we want to be strict
          range.deleteContents();
        }
      } catch (err) {
        console.warn("Failed to remove brace", err);
      }
      setTriggeredByBrace(false);
    }
    insertTagAtCursor(tagValue);
    setTagMenuAnchorEl(null);
  };

  const showTagButton = onTagClick || tags;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        component="div"
        ref={rootRef}
        className={className}
        contentEditable
        suppressContentEditableWarning
        spellCheck
        sx={{
        minHeight: computedMinHeight,
        maxHeight: computedMaxHeight,
        borderRadius: '12px',
        border: '1px solid #ddd',
        p: 1.25,
        pr: showTagButton ? 5 : 1.25,
        '&:focus': { outline: `2px solid rgba(87,87,176,0.1)` },
        whiteSpace: isSingleLine ? 'nowrap' : 'pre-wrap',
        overflowX: isSingleLine ? 'auto' : 'hidden',
        overflowY: isSingleLine ? 'hidden' : 'auto',
        '& .tag-chip': {
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '12px',
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          marginRight: '6px'
        },
        '& .tag-chip-escaped': {
          backgroundColor: 'transparent'
        }
      }}
    />
      {isEmpty && placeholder && (
        <Box
          onClick={() => {
            if (rootRef.current) rootRef.current.focus();
          }}
          sx={{
            position: 'absolute',
            left: 12,
            top: isSingleLine ? 8 : 12,
            pointerEvents: 'auto',
            color: 'rgba(0,0,0,0.38)',
            fontSize: '0.95rem',
            userSelect: 'none',
            cursor: 'text'
          }}
        >{placeholder}</Box>
      )}
      {showTagButton && (
        <Tooltip title="Insert tag">
          <IconButton
            onMouseDown={(e) => {
              // Try to prevent focus loss from editor when clicking button
              e.preventDefault();
            }}
            onClick={onTagClick || handleInternalTagClick}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: isSingleLine ? '50%' : 8,
              transform: isSingleLine ? 'translateY(-50%)' : 'none',
              color: 'action.active',
              zIndex: 1
            }}
          >
            <LocalOfferIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {tags && (
        <TagsDropdown
          tags={tags}
          handleTagSelect={handleInternalTagSelect}
          anchorEl={tagMenuAnchorEl}
          setAnchorEl={(el) => {
            setTagMenuAnchorEl(el);
            if (!el) setTriggeredByBrace(false);
          }}
          scope={scope}
        />
      )}
      <Dialog open={escapeDialogOpen} onClose={closeEscapeDialog}>
        <DialogTitle>
          {escapeTarget?.escaped
            ? t('tag_editor.unescape_title', 'Convert back to a tag?')
            : t('tag_editor.escape_title', 'Keep this tag as plain text?')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {escapeTarget?.escaped
              ? t('tag_editor.unescape_body', 'This will turn the plain text back into a real tag so it can be replaced with data.')
              : t('tag_editor.escape_body', 'If you choose “Keep as Text,” this tag will stay exactly as you typed it and will not be replaced with real data.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEscapeDialog}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleEscapeConfirm} variant="contained">
            {escapeTarget?.escaped
              ? t('tag_editor.unescape_confirm', 'Convert to Tag')
              : t('tag_editor.escape_confirm', 'Keep as Text')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default TagEditor;
